import { useMemo, useState } from "react"
import { useTranslation } from "@/i18n"
import type { PostFilterState } from "@/nostr/api/filters/filterEngine"
import type { TopicMode } from "@/nostr/api/filters/topicFilter"
import { TOPIC_LABELS, searchTopics, type TopicId } from "@/nostr/api/filters/topicLexicon"
import { Icon } from "@/components/Icon"
import { FilterField } from "./FilterSection"

export interface TopicsFilterProps {
  topics: TopicId[]
  mode: TopicMode
  onChange: (patch: Partial<PostFilterState>) => void
}

/**
 * TOPICS: the subject-matter axis of the content filters.
 *
 * A searchable multi-select over the topic lexicon. Selections are combined with
 * OR and can be kept ("Only these topics") or hidden ("Hide these topics"); the
 * "All topics" chip clears the selection. Selected topics stay visible at the top
 * as removable chips so a long vocabulary never hides the current state.
 */
export function TopicsFilter({ topics, mode, onChange }: TopicsFilterProps) {
  const { t } = useTranslation()
  const [term, setTerm] = useState("")
  const visible = useMemo(() => searchTopics(term), [term])
  const selected = useMemo(() => new Set(topics), [topics])

  const toggle = (topic: TopicId) => {
    onChange({
      topics: selected.has(topic) ? topics.filter((item) => item !== topic) : [...topics, topic],
    })
  }

  const clear = () => onChange({ topics: [] })

  return (
    <FilterField
      label={t("Topics")}
      hint={
        mode === "exclude"
          ? t("Posts detected as any of these topics are hidden. Uncategorized posts stay visible.")
          : t("Topics come from hashtags, title and text. Uncategorized posts are never hidden by a topic selection.")
      }
    >
      <div className="filter-topic-toolbar">
        <select
          value={mode}
          onChange={(event) => onChange({ topicMode: event.target.value as TopicMode })}
          aria-label={t("Topic filter mode")}
        >
          <option value="include">{t("Only these topics")}</option>
          <option value="exclude">{t("Hide these topics")}</option>
        </select>
        {topics.length > 0 && (
          <button type="button" className="btn btn-ghost btn-small" onClick={clear}>
            <Icon name="x" size={13} />
            {t("Clear topics")}
          </button>
        )}
      </div>

      <div className="search-input filter-topic-search">
        <Icon name="search" size={16} />
        <input
          type="text"
          value={term}
          onChange={(event) => setTerm(event.target.value)}
          placeholder={t("Search topics")}
          aria-label={t("Search topics")}
          spellCheck={false}
        />
        {term && (
          <button type="button" className="icon-button small" onClick={() => setTerm("")} aria-label={t("Clear")}>
            <Icon name="x" size={14} />
          </button>
        )}
      </div>

      {topics.length > 0 && (
        <div className="filter-selected-topics" role="list" aria-label={t("Selected topics")}>
          {topics.map((topic) => (
            <button
              key={topic}
              type="button"
              role="listitem"
              className="chip filter-chip active"
              aria-label={t("Remove topic: {topic}", { topic: t(TOPIC_LABELS[topic]) })}
              onClick={() => toggle(topic)}
            >
              {t(TOPIC_LABELS[topic])}
              <Icon name="x" size={11} />
            </button>
          ))}
        </div>
      )}

      <div className="filter-chip-group filter-topic-chips" role="group" aria-label={t("Topics")}>
        <button
          type="button"
          className={`chip filter-chip ${topics.length === 0 ? "active" : ""}`.trim()}
          aria-pressed={topics.length === 0}
          onClick={clear}
        >
          {t("All topics")}
        </button>
        {visible.map((topic) => {
          const active = selected.has(topic)
          return (
            <button
              key={topic}
              type="button"
              className={`chip filter-chip ${active ? "active" : ""}`.trim()}
              aria-pressed={active}
              onClick={() => toggle(topic)}
            >
              {t(TOPIC_LABELS[topic])}
            </button>
          )
        })}
      </div>

      {visible.length === 0 && <p className="muted small">{t("No topic matches that search.")}</p>}
      {term && visible.length > 0 && (
        <p className="muted small">{t("{n} topics match", { n: visible.length })}</p>
      )}
    </FilterField>
  )
}
